import React from 'react';
import { ICONS } from '../constants';
import { useCredits } from '../contexts';
import { SUBSCRIPTION_TIERS } from '../services/firebase';
import { SubscriptionTier } from '../types';

interface NotEnoughCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredCredits?: number;
  showPlansOnly?: boolean;
  onUpgradeClick?: (tier: Exclude<SubscriptionTier, 'free'>) => void | Promise<void>;
  upgradingTier?: Exclude<SubscriptionTier, 'free'> | null;
}

const NotEnoughCreditsModal: React.FC<NotEnoughCreditsModalProps> = ({ 
  isOpen, 
  onClose, 
  requiredCredits = 0,
  showPlansOnly = false,
  onUpgradeClick,
  upgradingTier = null
}) => {
  const { credits, subscription } = useCredits();

  if (!isOpen) return null;

  const getUpgradeOptions = () => {
    if (subscription === 'free') {
      return [
        { tier: 'creator', ...SUBSCRIPTION_TIERS.creator },
        { tier: 'viral', ...SUBSCRIPTION_TIERS.viral },
        { tier: 'studio', ...SUBSCRIPTION_TIERS.studio }
      ];
    }
    if (subscription === 'creator') {
      return [
        { tier: 'viral', ...SUBSCRIPTION_TIERS.viral },
        { tier: 'studio', ...SUBSCRIPTION_TIERS.studio }
      ];
    }
    if (subscription === 'viral') {
      return [{ tier: 'studio', ...SUBSCRIPTION_TIERS.studio }];
    }
    return [];
  };

  const upgradeOptions = getUpgradeOptions();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-click-card border border-click-border rounded-2xl max-w-lg w-full p-6 sm:p-8 relative m-4 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 left-4 text-gray-500 hover:text-white transition-colors"
        >
          <ICONS.X className="w-6 h-6" />
        </button>

        {showPlansOnly ? (
          <div className="text-center mb-8 pt-2">
            <h2 className="text-2xl font-bold text-white mb-2">שדרג חבילה</h2>
            <p className="text-gray-400">בחר תוכנית כדי לקבל יותר קרדיטים.</p>
          </div>
        ) : (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ICONS.AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">אין מספיק קרדיטים</h2>
            <p className="text-gray-400">
              נדרשים <span className="text-click-red font-bold">{requiredCredits}</span> קרדיטים ליצירת תמונה.
              <br />
              יש לך כרגע <span className="text-white font-bold">{credits.toLocaleString()}</span> קרדיטים.
            </p>
          </div>
        )}

        {upgradeOptions.length > 0 ? (
          <>
            <p className="text-center text-gray-400 text-sm mb-6">
              שדרג את החבילה שלך כדי לקבל יותר קרדיטים:
            </p>
            <div className="space-y-3">
              {upgradeOptions.map((option) => (
                <button
                  type="button"
                  key={option.tier}
                  onClick={() => onUpgradeClick?.(option.tier as Exclude<SubscriptionTier, 'free'>)}
                  disabled={upgradingTier !== null}
                  className={`p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${
                    option.tier === 'studio'
                      ? 'border-yellow-400/50 bg-yellow-400/5 hover:bg-yellow-400/10' 
                      : option.tier === 'viral'
                      ? 'border-violet-400/50 bg-violet-400/5 hover:bg-violet-400/10'
                      : 'border-click-cyan/50 bg-click-cyan/5 hover:bg-click-cyan/10'
                  } ${upgradingTier !== null ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ICONS.Crown className={`w-5 h-5 ${
                        option.tier === 'studio'
                          ? 'text-yellow-400'
                          : option.tier === 'viral'
                          ? 'text-violet-300'
                          : 'text-click-cyan'
                      }`} />
                      <div>
                        <h3 className="font-bold text-white">{option.nameHe}</h3>
                        <p className="text-sm text-gray-400">
                          {option.monthlyCredits.toLocaleString()} קרדיטים לחודש
                          {option.priceMonthlyILS != null && option.priceMonthlyILS > 0 && (
                            <span className="text-white font-medium"> · ₪{option.priceMonthlyILS}/חודש</span>
                          )}
                        </p>
                      </div>
                    </div>
                    {upgradingTier === option.tier ? (
                      <ICONS.Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
                    ) : (
                      <ICONS.ArrowRight className="w-5 h-5 text-gray-400 rotate-180" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center">
            <p className="text-gray-400 mb-4">
              הקרדיטים יתחדשו בתחילת החודש הבא.
            </p>
            <button
              onClick={onClose}
              className="bg-click-border text-white px-6 py-2 rounded-lg hover:bg-click-border/80 transition-colors"
            >
              הבנתי
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotEnoughCreditsModal;
